import { i18n } from "../i18n/i18n";

const MODALS = ".overlay, .ch-sheet, .tr-sheet, .tr-confirm-wrap, .vip-confirm, .ad-sim";
const CONTROLS = 'button, a[href], input, select, textarea, summary, [tabindex]:not([tabindex="-1"])';

/** Shared focus management for menus, including dynamically rendered nested confirmations. */
export function installModalAccessibility(ui: HTMLElement): () => void {
  let active: HTMLElement | null = null;
  let serial = 0;
  const returnFocus = new WeakMap<HTMLElement, HTMLElement>();
  const inertBefore = new Map<HTMLElement, boolean>();
  const visible = (el: HTMLElement): boolean => !el.closest("[hidden]") && el.getClientRects().length > 0;
  const controls = (el: HTMLElement): HTMLElement[] => Array.from(el.querySelectorAll<HTMLElement>(CONTROLS))
    .filter((c) => visible(c) && !c.matches(":disabled") && !c.closest("[inert]") && c.tabIndex >= 0);
  const restoreInert = (): void => {
    inertBefore.forEach((value, el) => { el.inert = value; });
    inertBefore.clear();
  };
  const focusFirst = (el: HTMLElement): void => {
    const first = controls(el)[0];
    if (first) first.focus({ preventScroll: true });
    else { el.tabIndex = -1; el.focus({ preventScroll: true }); }
  };
  const refresh = (): void => {
    // Inert is reapplied after choosing the current visible modal.
    restoreInert();
    const candidates = Array.from(ui.querySelectorAll<HTMLElement>(MODALS)).filter(visible);
    const score = (el: HTMLElement): number => {
      let n = 0;
      for (let p: HTMLElement | null = el; p && p !== ui; p = p.parentElement) {
        if (p.matches(MODALS)) n += Number.parseInt(getComputedStyle(p).zIndex, 10) || 0;
      }
      return n;
    };
    let next: HTMLElement | null = null;
    for (const el of candidates) {
      if (!next || next.contains(el) || (!el.contains(next) && score(el) >= score(next))) next = el;
    }
    if (next !== active) {
      const previous = active;
      previous?.removeAttribute("aria-modal");
      active = next;
      if (next) {
        const saved = previous && returnFocus.get(previous);
        const focused = document.activeElement;
        if (saved && next.contains(saved) && visible(saved)) saved.focus({ preventScroll: true });
        else if (focused instanceof HTMLElement && !next.contains(focused)) {
          returnFocus.set(next, focused);
          focusFirst(next);
        }
      } else if (previous) {
        const saved = returnFocus.get(previous);
        if (saved?.isConnected && visible(saved)) saved.focus({ preventScroll: true });
      }
    }
    if (!active) return;
    if (!active.hasAttribute("role")) active.setAttribute("role", "dialog");
    active.setAttribute("aria-modal", "true");
    const title = active.querySelector<HTMLElement>("h1, h2, .vip-confirm-title, .trs-name, .cs-name, .trc-text");
    if (title) {
      if (!title.id) title.id = `vh-dialog-title-${++serial}`;
      active.setAttribute("aria-labelledby", title.id);
    }
    active.querySelectorAll<HTMLButtonElement>("button").forEach((button) => {
      if (button.textContent?.trim() === "✕" && !button.hasAttribute("aria-label")) button.setAttribute("aria-label", i18n.t("common.close"));
    });
    // Inert siblings at each level: both background HUD and underlying modal controls.
    for (let el: HTMLElement = active; el !== ui && el.parentElement; el = el.parentElement) {
      for (const sibling of Array.from(el.parentElement.children)) {
        if (sibling === el || !(sibling instanceof HTMLElement)) continue;
        inertBefore.set(sibling, sibling.inert);
        sibling.inert = true;
      }
    }
    if (!active.contains(document.activeElement)) focusFirst(active);
  };
  const observer = new MutationObserver((records) => {
    // HUD numbers change every frame. Only menu mutations require a rescan.
    if (records.some((r) => r.target instanceof Element && (r.target.closest(MODALS)
      || [...r.addedNodes, ...r.removedNodes].some((n) => n instanceof Element && (n.matches(MODALS) || n.querySelector(MODALS)))))) refresh();
  });
  observer.observe(ui, { subtree: true, childList: true, attributes: true, attributeFilter: ["hidden"] });
  const onKey = (e: KeyboardEvent): void => {
    if (e.key !== "Tab" || !active) return;
    const list = controls(active);
    const first = list[0], last = list[list.length - 1];
    const focused = document.activeElement;
    if (!first) { e.preventDefault(); active.focus(); }
    else if (e.shiftKey && (focused === first || !list.includes(focused as HTMLElement))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (focused === last || !list.includes(focused as HTMLElement))) { e.preventDefault(); first.focus(); }
  };
  ui.addEventListener("keydown", onKey);
  refresh();
  return () => { observer.disconnect(); ui.removeEventListener("keydown", onKey); restoreInert(); };
}
